import { theme } from 'gatsby-theme-fela';
import { useCallback, useEffect, useRef, useState } from 'react';

import { BackgroundMode, DELAY_MS, NUMBER_OF_FRAMES } from '../constants';
import { getBackgroundUrl } from '../utils';

export const useBackgroundPixelation = (intersecting: boolean) => {
    const currentStep = useRef(0);
    const animationRequest = useRef<number>();
    const previousFrameTimestamp = useRef<number>();

    const [backgroundCSS, setBackgroundCSS] = useState<string>(theme.colors.white);
    const [backgroundMode, setBackgroundMode] = useState<BackgroundMode>(BackgroundMode.Light);

    const animatePixelation = useCallback(
        (timestamp: number) => {
            if (intersecting && currentStep.current === NUMBER_OF_FRAMES) return;
            if (!intersecting && currentStep.current < 0) return;

            const prevTimestamp = previousFrameTimestamp.current ?? timestamp - DELAY_MS;

            // Only render a new frame if the delay has passed.
            // This is to ensure the same animation speed regardless of the frame rate of the screen.
            if (timestamp - prevTimestamp >= DELAY_MS) {
                const bgUrl = getBackgroundUrl(currentStep.current);
                setBackgroundCSS(bgUrl);

                intersecting ? currentStep.current++ : currentStep.current--;

                previousFrameTimestamp.current = timestamp;
            }

            // Recursively request the next frame
            animationRequest.current = requestAnimationFrame(animatePixelation);
        },
        [intersecting],
    );

    useEffect(() => {
        animationRequest.current = requestAnimationFrame(animatePixelation);

        if (intersecting) {
            setBackgroundMode(BackgroundMode.Dark);
        } else {
            setBackgroundMode(BackgroundMode.Light);
        }

        return () => {
            if (animationRequest.current) {
                cancelAnimationFrame(animationRequest.current);
                previousFrameTimestamp.current = undefined;
            }
        };
    }, [intersecting, animatePixelation]);

    return { backgroundCSS, backgroundMode };
};
